/**
 * DownloadMenu — téléchargement d'un résultat dans plusieurs formats.
 *
 * Les conversions purement navigateur (canvas → JPEG/WebP, Web Audio → WAV)
 * sont proposées quand elles sont possibles ; sinon on télécharge le fichier
 * original tel quel. Pas d'encodeur externe : MP3/GIF exigeraient une
 * dépendance, hors périmètre (zéro runtime dependency).
 */
import { h } from "./dom.js";

export interface DownloadOption {
	label: string;
	run: () => void;
}

/** Bouton "Télécharger" ouvrant un menu des formats disponibles. */
export function downloadMenu(options: DownloadOption[], buttonLabel = "Enregistrer"): HTMLElement {
	let popup: HTMLElement | null = null;
	const btn = h("button", { class: "btn btn-small", type: "button" }, buttonLabel);

	function close(): void {
		popup?.remove();
		popup = null;
		document.removeEventListener("mousedown", onDocDown);
	}

	function onDocDown(event: MouseEvent): void {
		if (popup && !popup.contains(event.target as Node) && event.target !== btn) close();
	}

	btn.addEventListener("click", () => {
		if (popup) {
			close();
			return;
		}
		popup = h(
			"div",
			{ class: "download-menu-popup", role: "menu" },
			...options.map((opt) =>
				h(
					"button",
					{
						type: "button",
						role: "menuitem",
						onclick: () => {
							opt.run();
							close();
						},
					},
					opt.label
				)
			)
		);
		btn.parentElement?.appendChild(popup);
		document.addEventListener("mousedown", onDocDown);
	});

	return h("div", { class: "download-menu" }, btn);
}

/** Télécharge une URL sous un nom de fichier. */
export function downloadUrl(url: string, filename: string): void {
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
}

/** Options de téléchargement d'une image : originale + conversions canvas. */
export function imageDownloadOptions(url: string, filename: string): DownloadOption[] {
	const base = filename.replace(/\.[^.]+$/, "");
	const original = filename.match(/\.[^.]+$/)?.[0] ?? ".png";
	const options: DownloadOption[] = [
		{ label: `Original (${original.toUpperCase()})`, run: () => downloadUrl(url, filename) },
	];
	for (const mime of ["image/png", "image/jpeg", "image/webp"]) {
		const ext = mime.split("/")[1].replace("jpeg", "jpg");
		if (`.${ext}` === original) continue;
		options.push({
			label: ext.toUpperCase(),
			run: () => void convertImage(url, mime, `${base}.${ext}`),
		});
	}
	return options;
}

async function convertImage(url: string, mime: string, filename: string): Promise<void> {
	const img = new Image();
	img.crossOrigin = "anonymous";
	await new Promise<void>((resolve, reject) => {
		img.onload = () => resolve();
		img.onerror = () => reject(new Error("Chargement de l'image impossible"));
		img.src = url;
	});
	const canvas = document.createElement("canvas");
	canvas.width = img.naturalWidth;
	canvas.height = img.naturalHeight;
	const ctx = canvas.getContext("2d");
	if (!ctx) return;
	ctx.drawImage(img, 0, 0);
	const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, 0.92));
	if (!blob) return;
	const objectUrl = URL.createObjectURL(blob);
	downloadUrl(objectUrl, filename);
	URL.revokeObjectURL(objectUrl);
}
